import React, { useMemo, useEffect, useLayoutEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import Link from '@atlaskit/link';
import CrossIcon from '@atlaskit/icon/core/cross';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { getAdjustedTopPropertyValue } from './utils/getAdjustedTopPropertyValue.ts';
import { BannerContainer, Banner, IconContainer } from './styled/index.ts';
import type { EditorBannerProps, EditorBannerContainerProps } from './OnboardingEditorBanner.tsx';

const i18n = defineMessages({
	// eslint-disable-next-line @atlassian/i18n/require-description
	bannerMessage: {
		id: 'onboarding-editor.draft.banner',
		defaultMessage:
			'{boldedUnpublishedDraftText} Only you can see it until you publish it or give someone the link. {learnMoreLink}',
	},
	unpublishedDraftText: {
		id: 'onboarding-editor.unpublished.draft',
		defaultMessage: '<b>This is an unpublished draft.</b>',
		description: 'This is an unpublished draft.',
	},
	learnMore: {
		id: 'onboarding-editor.learn.more',
		defaultMessage: 'Learn more',
		description: 'Learn more about drafts',
	},
});

export const EditorDraftBanner = (props: EditorBannerProps & EditorBannerContainerProps) => {
	const {
		isContentCreationPanelOpen,
		showingOfflineBanner,
		setIsVisible,
		deserializedData: { wasDraftBannerSeen } = {},
		setIsOnboardingBannerDataLoaded,
	} = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();

	useLayoutEffect(() => {
		if (expValEquals('cc_editor_ttvc_release_bundle_one', 'onboardingUseEffectFix', true)) {
			if (!wasDraftBannerSeen) {
				const editorNode: HTMLElement | null = document.querySelector('.ak-editor-content-area');

				if (editorNode) {
					editorNode.style.marginTop = '52px';
				}

				return () => {
					if (editorNode) editorNode.style.marginTop = '0';
				};
			}
		}
	}, [wasDraftBannerSeen]);

	// Adjusting the margins after mount in this useEffect is causing a TTVC regression. Remove this useEffect after removing cc_editor_ttvc_release_bundle_one.
	useEffect(() => {
		if (!expValEquals('cc_editor_ttvc_release_bundle_one', 'onboardingUseEffectFix', true)) {
			if (!wasDraftBannerSeen) {
				const editorNode: HTMLElement | null = document.querySelector('.ak-editor-content-area');

				if (editorNode) {
					editorNode.style.marginTop = '52px';
				}

				return () => {
					if (editorNode) editorNode.style.marginTop = '0';
				};
			}
		}
	}, [wasDraftBannerSeen]);

	useEffect(() => {
		if (!wasDraftBannerSeen) {
			void setOnboardingState([
				{
					key: EDITOR_ONBOARDING_STATE_KEYS.WAS_DRAFT_BANNER_SEEN,
					value: 'true',
				},
				{
					key: EDITOR_ONBOARDING_STATE_KEYS.IS_DRAFT_BANNER_VISIBLE,
					value: 'true',
				},
			]);

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'onboardingEditorDraft',
				},
			}).fire();
			// TODO: Refactor https://hello.atlassian.net/browse/CONFONBO-1674
			setTimeout(() => {
				setIsOnboardingBannerDataLoaded(true);
			}, 0);
		}
	}, [
		createAnalyticsEvent,
		setIsOnboardingBannerDataLoaded,
		setOnboardingState,
		wasDraftBannerSeen,
	]);

	const _onDismiss = () => {
		setIsVisible(false);
		// Move all the content back up 52px to adjust for the banner disappearing
		const editorNode: HTMLElement | null = document.querySelector('.ak-editor-content-area');

		if (editorNode) {
			editorNode.style.marginTop = '0px';
		}
	};

	// compiled shouldn't handle a complicated runtime CSS, hence the inlined style
	const calculatedTop = useMemo(
		() => getAdjustedTopPropertyValue(showingOfflineBanner),
		[showingOfflineBanner],
	);

	return (
		<BannerContainer
			style={{ top: calculatedTop }}
			isContentCreationPanelOpen={isContentCreationPanelOpen}
			data-testid="onboarding-editor-draft-banner"
		>
			<Banner>
				<FormattedMessage
					{...i18n.bannerMessage}
					values={{
						boldedUnpublishedDraftText: (
							<FormattedMessage
								{...i18n.unpublishedDraftText}
								values={{ b: (chunks) => <b>{chunks}</b> }}
							/>
						),
						learnMoreLink: fg('dst-a11y__replace-anchor-with-link__flywheel-adopt') ? (
							<Link
								href="https://support.atlassian.com/confluence-cloud/docs/add-or-remove-page-restrictions/"
								target="_blank"
							>
								<FormattedMessage {...i18n.learnMore} />
							</Link>
						) : (
							// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
							<a
								href="https://support.atlassian.com/confluence-cloud/docs/add-or-remove-page-restrictions/"
								target="_blank"
							>
								<FormattedMessage {...i18n.learnMore} />
							</a>
						),
					}}
				/>
				<IconContainer data-testid="onboarding-editor-close-banner" onClick={_onDismiss}>
					<CrossIcon label="dismiss" />
				</IconContainer>
			</Banner>
		</BannerContainer>
	);
};
